import { PROFILER_SEARCH_FIELDS } from '../../../../utils/constants/fields';
import { DEFAULT_PROFILER_VIEW } from '../../../../utils/constants/profilerViews';
import { UI_OPTION_KEYS } from '../../../../utils/constants/uiOptionKeys';
import { UI_OPTIONS } from '../../../../utils/constants/uiOptions';
import { mapFieldsToState } from '../../../../utils/utils';
import {
  readSavedProfilerColumns,
  withNewProfilerColumns
} from '../../../../utils/constants/profilerColumns';

export const getProfilerColumnsBySearchResultsPage = (searchId = null) => {
  const profilerColumns = readSavedProfilerColumns(`pcbsrp-${searchId}`);
  if (profilerColumns) {
    return withNewProfilerColumns(profilerColumns, getInitialProfilerColumns());
  }

  return null;
};

export const saveProfilerColumnsBySearchResultsPage = (profilerColumns, searchId) => {
  if (!profilerColumns?.length || !searchId) {
    return;
  }

  localStorage.setItem(`pcbsrp-${searchId}`, JSON.stringify(profilerColumns));
};

export const getProfilerExtendedFinancialsToggleBySearchResultsPage = (searchId) => {
  const isToggled = localStorage.getItem(`peftbsrp-${searchId}`);
  if (isToggled) {
    return JSON.parse(isToggled);
  }

  return false;
};

export const saveProfilerExtendedFinancialsToggleBySearchResultsPage = (isToggled, searchId) => {
  if (typeof isToggled !== 'boolean' || !searchId) {
    return;
  }

  localStorage.setItem(`peftbsrp-${searchId}`, JSON.stringify(isToggled));
};

export const getProfilerSortSelectionBySearchResultsPage = (searchId) => {
  return localStorage.getItem(`pssbsrp-${searchId}`) || 'default';
};

export const saveProfilerSortSelectionBySearchResultsPage = (selectedOption, searchId) => {
  if (typeof selectedOption !== 'string' || !searchId) {
    return;
  }

  localStorage.setItem(`pssbsrp-${searchId}`, selectedOption);
};

/**
 * The stored Profiler view for a result set, or `fallbackView` when the user has
 * never picked one for it.
 *
 * The fallback is a parameter because the standard view is not the same on every
 * surface. Search results, saved projects and team feedback stack the heatmap over
 * the list, so the heatmap is a superset of the list there and is their default
 * (TD-2720 / TD-2733). The Comparable companies tab still renders the three views
 * exclusively, so a heatmap default hides the table outright — it passes List
 * instead (TD-2838). Defaulting the parameter keeps every other caller unchanged.
 */
export const getProfilerViewSelectionBySearchResultsPage = (
  searchId,
  fallbackView = DEFAULT_PROFILER_VIEW
) => {
  return localStorage.getItem(`pvsbsp-${searchId}`) || fallbackView;
};

export const saveProfilerViewSelectionBySearchResultsPage = (selectedView, searchId) => {
  if (typeof selectedView !== 'string' || !searchId) {
    return;
  }

  localStorage.setItem(`pvsbsp-${searchId}`, selectedView);
};

export const getProfilerPreFinancialsOnlySelectionsBySearchResultsPage = (searchId) => {
  const previousSelections = localStorage.getItem(`ppfosbsrp-${searchId}`);
  if (previousSelections) {
    return JSON.parse(previousSelections);
  }

  return null;
};

export const saveProfilerPreFinancialsOnlySelectionsBySearchResultsPage = (
  selections,
  searchId
) => {
  if (!selections?.length || !searchId) {
    return;
  }

  localStorage.setItem(`ppfosbsrp-${searchId}`, JSON.stringify(selections));
};

export const getProfilerHeatmapTileSizeSelectionBySearchResultsPage = (searchId) => {
  return (
    localStorage.getItem(`phtssbsrp-${searchId}`) ||
    UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_SIZE].Sales
  );
};

export const saveProfilerHeatmapTileSizeSelectionBySearchResultsPage = (size, searchId) => {
  if (typeof size !== 'string' || !searchId) {
    return;
  }

  localStorage.setItem(`phtssbsrp-${searchId}`, size);
};

export const getProfilerHeatmapColorCodingSelectionBySearchResultsPage = (searchId) => {
  return (
    localStorage.getItem(`phccsbsrp-${searchId}`) ||
    UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING]['EBIT Margin (%)']
  );
};

// TD-2499: Profiler Lite has no EBIT data, so a restored/defaulted heatmap EBIT
// selection (the persistence helpers above fall back to EBIT when nothing is
// stored) must be coerced to the Lite-safe metric — otherwise the control shows a
// value that isn't in its filtered option list and the heatmap colours by absent
// data. CAGR / Sales are the Lite-safe equivalents.
export const coerceHeatmapColorCodingForLite = (value, isProfilerLiteOnly) =>
  isProfilerLiteOnly &&
  value === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING]['EBIT Margin (%)']
    ? UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING]['CAGR 3Y (%)']
    : value;
export const coerceHeatmapTileSizeForLite = (value, isProfilerLiteOnly) =>
  isProfilerLiteOnly && value === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_SIZE].EBIT
    ? UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_SIZE].Sales
    : value;

/**
 * Drop EBIT heatmap tile-size / colour-coding options when the viewer has no
 * EBIT access (Lite). Shared by search, projects, and comparables to avoid CPD.
 *
 * @param {Array<{value: string}>} options
 * @param {boolean} hasEbitAccess
 */
export const filterHeatmapTileSizeOptionsForAccess = (options, hasEbitAccess) =>
  hasEbitAccess
    ? options
    : options.filter((o) => o.value !== UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_SIZE].EBIT);

export const filterHeatmapColorCodingOptionsForAccess = (options, hasEbitAccess) =>
  hasEbitAccess
    ? options
    : options.filter(
        (o) =>
          o.value !== UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING]['EBIT Margin (%)']
      );

// TD-2499: localStorage column sets / column-filters can pre-date Lite gating
// (saved while Full, or before this deploy). Mirror the heatmap coerce so a
// restored EBIT-margin column or ebitPercentage filter cannot leak into Lite UI
// or silently drop rows with missing EBIT values.
export const coerceProfilerColumnsForLite = (columns, hideEbitMargin) => {
  if (!hideEbitMargin || !Array.isArray(columns)) return columns;
  // EBIT_MARGIN id is shared by search + project column enums.
  return columns.filter((col) => col?.id !== 'EBIT_MARGIN');
};

export const coerceColumnFiltersForLite = (filters, hideEbitMargin) => {
  if (!hideEbitMargin || !filters || typeof filters !== 'object') return filters || {};
  if (!Object.hasOwn(filters, 'ebitPercentage')) return filters;
  const next = { ...filters };
  delete next.ebitPercentage;
  return next;
};

/** The two EBIT-margin entries of `profilerSearchSortOptions`, ascending and descending. */
export const EBIT_MARGIN_SORT_VALUES = ['_ebitPercentage-asc', '_ebitPercentage-desc'];

/**
 * TD-2890: does this result set actually carry EBIT margins?
 *
 * Deliberately keyed on the data rather than on the viewer's tier. The BE withholds the
 * whole `ebit` block from accounts without EBIT entitlement (measured on DEV: a Lite
 * account gets 0/140 rows with `_ebitPercentage` while revenue lands on 137/140), so the
 * data already answers the question — and it answers it correctly no matter which tier
 * rule the BE applies. Deriving the rule a second time in the FE is what let the sort
 * menus drift out of step with what the BE serves.
 *
 * A set where nothing carries a margin makes the EBIT sorts a no-op, so hiding them is
 * right on its own terms, entitlement aside.
 *
 * Mirrors `ebitMarginOf` in `ProfilerBarChart/chartSortDefaults`: null, undefined, '' and
 * NaN all count as absent.
 *
 * @param {Array<{_ebitPercentage: *}>} results
 */
export const hasEbitMarginData = (results) =>
  Array.isArray(results) &&
  results.some((row) => {
    const raw = row?._ebitPercentage;
    if (raw === null || raw === undefined || raw === '') return false;
    return !Number.isNaN(Number(raw));
  });

/**
 * Drop the EBIT-margin sort entries. The caller supplies the predicate: the search,
 * project and team views pass `hasEbitMarginData(results)`; comparables passes its own
 * `hasExtendedAccess`, which is what already drives its EBIT columns and chart view, so
 * its sort menu stays consistent with the surface around it.
 *
 * Applied to the option list rather than inside the menus' own filter: those filter by
 * *visible column* and deliberately bypass that below 1366 / 1536px, where the column set
 * is a priority-limited subset and filtering sorts by it would strip Sales / CAGR too.
 * Gating the list means the width branches and the Chart branch inherit it, and
 * `getSortTooltipText` cannot resolve an EBIT label either.
 *
 * @param {Array<{value: string}>} options
 * @param {boolean} showEbitSorts
 */
export const filterEbitSortOptions = (options, showEbitSorts) =>
  showEbitSorts ? options : options.filter((o) => !EBIT_MARGIN_SORT_VALUES.includes(o.value));

/**
 * A persisted sort can pre-date the filter above, or survive a plan change. Coerce the
 * *displayed* sort rather than the stored one: applying an EBIT sort to a set with no
 * margins is already a no-op, so the only artefact is a Sort button left lit on an entry
 * the menu no longer offers, with its tooltip falling back to 'Sort' and no way to clear
 * it. Deriving it keeps this self-correcting as results load, which a one-shot reset of
 * the stored value could not.
 */
export const coerceSortSelectionWithoutEbitData = (value, hasEbitData) =>
  !hasEbitData && EBIT_MARGIN_SORT_VALUES.includes(value) ? 'default' : value;

/** The two register-status entries of `profilerSearchSortOptions`. */
export const REGISTER_STATUS_SORT_VALUES = [
  '_registerStatusRank-asc',
  '_registerStatusNonActiveRank-desc'
];

/**
 * Same gate as the EBIT sorts, keyed on the data: a set where no company has a known
 * register status (an industry-code search until the backend delivers it, or an environment
 * whose data has none yet) makes these sorts a no-op, so they are not offered. The Add filter
 * panel hides the register status filter on the same condition (`hasKnownRegisterStatus`).
 *
 * @param {Array<{value: string}>} options
 * @param {boolean} showRegisterStatusSorts
 */
export const filterRegisterStatusSortOptions = (options, showRegisterStatusSorts) =>
  showRegisterStatusSorts
    ? options
    : options.filter((o) => !REGISTER_STATUS_SORT_VALUES.includes(o.value));

/** Display-only coercion for a persisted register-status sort, as for the EBIT sorts above. */
export const coerceSortSelectionWithoutRegisterStatusData = (value, hasRegisterStatusData) =>
  !hasRegisterStatusData && REGISTER_STATUS_SORT_VALUES.includes(value) ? 'default' : value;

/**
 * Both data gates on a sort menu, in one place for the search, project and team views: the
 * EBIT-margin sorts need margins in the set, the register-status sorts a known status.
 *
 * @param {Array<{value: string}>} options
 * @param {{hasEbitData: boolean, hasRegisterStatusData: boolean}} data
 */
export const gateProfilerSortOptions = (options, { hasEbitData, hasRegisterStatusData }) =>
  filterRegisterStatusSortOptions(
    filterEbitSortOptions(options, hasEbitData),
    hasRegisterStatusData
  );

/**
 * The sort shown on the Sort button, with both data gates applied to a persisted selection.
 *
 * @param {string} value the effective (Chart-aware) sort
 * @param {{hasEbitData: boolean, hasRegisterStatusData: boolean}} data
 */
export const coerceDisplayedProfilerSort = (value, { hasEbitData, hasRegisterStatusData }) =>
  coerceSortSelectionWithoutRegisterStatusData(
    coerceSortSelectionWithoutEbitData(value, hasEbitData),
    hasRegisterStatusData
  );

export const saveProfilerHeatmapColorCodingSelectionBySearchResultsPage = (
  colorCoding,
  searchId
) => {
  if (typeof colorCoding !== 'string' || !searchId) {
    return;
  }

  localStorage.setItem(`phccsbsrp-${searchId}`, colorCoding);
};

export const hasSavedProfilerHeatmapGroupingToggleBySearchResultsPage = (searchId) => {
  const saved = localStorage.getItem(`phgtbsrp-${searchId}`);

  return !!saved;
};

export const getProfilerHeatmapGroupingToggleBySearchResultsPage = (searchId) => {
  const isToggled = localStorage.getItem(`phgtbsrp-${searchId}`);
  if (isToggled) {
    return JSON.parse(isToggled);
  }

  return false;
};

export const saveProfilerHeatmapGroupingToggleBySearchResultsPage = (isToggled, searchId) => {
  if (typeof isToggled !== 'boolean' || !searchId) {
    return;
  }

  localStorage.setItem(`phgtbsrp-${searchId}`, JSON.stringify(isToggled));
};

// TD-2720 — the heatmap now sits above the list and can be collapsed away for a
// full-height list. That choice is per search, like the view, tile size, colour
// coding and grouping selections above it: an analyst who folds the heatmap on one
// search should not find it folded on the next one they open.
export const getProfilerHeatmapCollapsedBySearchResultsPage = (searchId) => {
  const isCollapsed = localStorage.getItem(`phcolsbsrp-${searchId}`);
  if (isCollapsed) {
    return JSON.parse(isCollapsed);
  }

  return false;
};

export const saveProfilerHeatmapCollapsedBySearchResultsPage = (isCollapsed, searchId) => {
  if (typeof isCollapsed !== 'boolean' || !searchId) {
    return;
  }

  localStorage.setItem(`phcolsbsrp-${searchId}`, JSON.stringify(isCollapsed));
};

/* --------------------------------------------------------------------------
 * V1 (TD-2200) — Active Spotlights + Column-value filters persistence.
 *
 * Active spotlights are stored as a JSON array of IDs. Column filters carry
 * Set instances (`values`, `keywords`) which JSON.stringify would flatten to
 * `{}`, so we serialize via _columnFiltersToJson / _columnFiltersFromJson.
 * -------------------------------------------------------------------------- */

export const getActiveSpotlightsBySearchResultsPage = (searchId) => {
  const raw = localStorage.getItem(`asbsrp-${searchId}`);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const saveActiveSpotlightsBySearchResultsPage = (spotlightIds, searchId) => {
  if (!Array.isArray(spotlightIds) || !searchId) return;
  localStorage.setItem(`asbsrp-${searchId}`, JSON.stringify(spotlightIds));
};

/**
 * Spread anything iterable (native Set, Array, MobX `ObservableSet`) into
 * a plain Array for JSON serialization. The previous `instanceof Set`
 * check was the V1.10 bug — once `state.columnFilters` is assigned a
 * filter that contains a Set, MobX deep-observability wraps the Set in
 * `ObservableSet`. `ObservableSet implements Set` but does NOT extend it,
 * so `instanceof Set` returns false and the values silently disappeared
 * on save. ObservableSet IS iterable, so a duck-typed check works for
 * all three shapes.
 */
const _toArrayOrNull = (maybeIterable) => {
  if (maybeIterable == null) return null;
  if (typeof maybeIterable[Symbol.iterator] !== 'function') return null;
  const arr = [...maybeIterable];
  return arr.length > 0 ? arr : null;
};

// Per-filter serializer extracted so `_columnFiltersToJson` stays under
// Sonar's CC-15 budget. Returns a plain JSON-safe object (Sets flattened to
// Arrays) — empty fields are dropped so we don't write `{ values: [] }`.
const _serializeFilter = (f) => {
  const obj = {};
  if (f.range) obj.range = { ...f.range };
  const valuesArr = _toArrayOrNull(f.values);
  if (valuesArr) obj.values = valuesArr;
  if (f.text != null) obj.text = f.text;
  const keywordsArr = _toArrayOrNull(f.keywords);
  if (keywordsArr) obj.keywords = keywordsArr;
  if (f.mode) obj.mode = f.mode;
  return obj;
};

// Exported so the Profiler-project-view helpers (V2.0) can reuse the same
// Set ⇄ Array (de)serialization without duplicating it. Same input shape
// (`state.columnFilters` from MobX, including ObservableSet-wrapped Sets)
// and same output shape (plain JSON-safe object).
export const _columnFiltersToJson = (columnFilters) => {
  if (!columnFilters || typeof columnFilters !== 'object') return {};
  const out = {};
  for (const key of Object.keys(columnFilters)) {
    const f = columnFilters[key];
    if (!f) continue;
    const obj = _serializeFilter(f);
    if (Object.keys(obj).length > 0) out[key] = obj;
  }
  return out;
};

// Per-filter hydrator — mirrors `_serializeFilter` for the JSON-to-state
// direction (Array → Set, empty fields dropped). Keeps `_columnFiltersFromJson`
// at CC < 15.
const _hydrateFilter = (f) => {
  const obj = {};
  if (f.range) obj.range = { ...f.range };
  if (Array.isArray(f.values) && f.values.length > 0) obj.values = new Set(f.values);
  if (f.text) obj.text = f.text;
  if (Array.isArray(f.keywords) && f.keywords.length > 0) obj.keywords = new Set(f.keywords);
  if (f.mode) obj.mode = f.mode;
  return obj;
};

// Exported alongside `_columnFiltersToJson` so the project-view helpers
// (V2.0) can reuse identical hydration semantics — array-to-Set inflation,
// empty-Set / empty-string drop, etc.
export const _columnFiltersFromJson = (json) => {
  if (!json || typeof json !== 'object') return {};
  const out = {};
  for (const key of Object.keys(json)) {
    const f = json[key];
    if (!f) continue;
    const obj = _hydrateFilter(f);
    if (Object.keys(obj).length > 0) out[key] = obj;
  }
  return out;
};

export const getColumnFiltersBySearchResultsPage = (searchId) => {
  const raw = localStorage.getItem(`cfbsrp-${searchId}`);
  if (!raw) return {};
  try {
    return _columnFiltersFromJson(JSON.parse(raw));
  } catch {
    return {};
  }
};

export const saveColumnFiltersBySearchResultsPage = (columnFilters, searchId) => {
  if (!searchId) return;
  localStorage.setItem(`cfbsrp-${searchId}`, JSON.stringify(_columnFiltersToJson(columnFilters)));
};

export const PROFILER_SEARCH_RESULTS_COLUMNS = {
  COMPANY_NAME: 'COMPANY_NAME',
  LOCATION: 'LOCATION',
  INDUSTRY: 'INDUSTRY',
  VALUE_CHAIN: 'VALUE_CHAIN',
  PRODUCTS: 'PRODUCTS',
  SALES: 'SALES',
  EMPLOYEES: 'EMPLOYEES',
  CAGR3Y: 'CAGR3Y',
  CAGR4Y: 'CAGR4Y',
  CAGR_PERIOD: 'CAGR_PERIOD',
  EBIT_MARGIN: 'EBIT_MARGIN',
  SITUATION: 'SITUATION',
  SHAREHOLDERS: 'SHAREHOLDERS',
  ACTIONS: 'ACTIONS',
  COMPANY_DESCRIPTION: 'COMPANY_DESCRIPTION',
  PRODUCT_PORTFOLIO_DESCRIPTION: 'PRODUCT_PORTFOLIO_DESCRIPTION',
  KEY_PRODUCTS: 'KEY_PRODUCTS',
  KEY_END_MARKETS: 'KEY_END_MARKETS'
  // TOP_MANAGEMENT: 'TOP_MANAGEMENT' //UNCOMMENT ONCE IMPLEMENTED ON BACKEND
};

export const getInitialProfilerColumns = (_searchFields, _windowWidth, hideEbitMargin = false) => {
  const searchFields = _searchFields || mapFieldsToState(PROFILER_SEARCH_FIELDS);

  const isEbitMarginFilled =
    !!searchFields[PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MIN.NAME] ||
    !!searchFields[PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MAX.NAME];
  const isCagr3YearsPeriodSelected =
    searchFields[PROFILER_SEARCH_FIELDS.CAGR_PERIOD.NAME]?.[0] === 'last3years';
  const isCagr4YearsPeriodSelected =
    searchFields[PROFILER_SEARCH_FIELDS.CAGR_PERIOD.NAME]?.[0] === 'last4years';
  const isSituationSelected = !!searchFields[PROFILER_SEARCH_FIELDS.SITUATION.NAME]?.length;
  const isOwnershipSelected =
    !!searchFields[PROFILER_SEARCH_FIELDS.OWNERSHIP_STRUCTURES.NAME]?.length ||
    !!searchFields[PROFILER_SEARCH_FIELDS.LISTING_STATUS.NAME]?.length;

  /*
   * RESPONSIVE COLUMN DISPLAY LOGIC
   * ================================
   * This implements the responsive column display requirements:
   *
   * 1. Width >= 1366px (Desktop): Show all user-selected columns
   *    - User can customize columns via drag-and-drop
   *    - Column customization panel is available
   *    - All selected columns are displayed
   *
   * 2. Width < 1366px (Tablet/Limited): Show exactly 8 columns
   *    - Base 7 columns (always shown): Company, Location, Industry, Value Chain, Products, Sales, Employees
   *    - Plus 1 priority column selected based on search filters using this hierarchy:
   *      a) EBIT Margin (if EBIT margin filter is filled)
   *      b) CAGR 3Y (if 3-year CAGR period is selected)
   *      c) CAGR 4Y (if 4-year CAGR period is selected)
   *      d) Situation (if situation filter is filled)
   *      e) Employees (default fallback - always shown if no other priority applies)
   *
   * 3. Financials Only Mode (triggered by toggle button):
   *    - Shows only financial columns: Company, Sales, Employees, CAGR 3Y, CAGR 4Y, EBIT Margin
   *    - Excludes operational columns: Location, Industry, Value Chain, Products, Situation
   *    - Works at all screen widths
   *
   * Note: default `selected` flags below follow filter-driven defaults; narrow-width
   * priority collapsing is applied by the results view when rendering.
   */

  const columns = [
    {
      id: PROFILER_SEARCH_RESULTS_COLUMNS.COMPANY_NAME,
      disableSelect: true,
      name: 'Company name',
      minWidth: '90px',
      fragment: '1.5fr',
      selected: true
    },
    {
      id: PROFILER_SEARCH_RESULTS_COLUMNS.LOCATION,
      name: 'Location',
      minWidth: '60px',
      fragment: '1fr',
      selected: true
    },
    {
      id: PROFILER_SEARCH_RESULTS_COLUMNS.COMPANY_DESCRIPTION,
      name: 'Company description',
      minWidth: '120px',
      fragment: '1.5fr',
      selected: false
    },
    {
      id: PROFILER_SEARCH_RESULTS_COLUMNS.PRODUCT_PORTFOLIO_DESCRIPTION,
      name: 'Portfolio description',
      minWidth: '120px',
      fragment: '1.5fr',
      selected: false
    },
    {
      id: PROFILER_SEARCH_RESULTS_COLUMNS.KEY_PRODUCTS,
      name: 'Key products',
      minWidth: '100px',
      fragment: '1fr',
      selected: false
    },
    {
      id: PROFILER_SEARCH_RESULTS_COLUMNS.KEY_END_MARKETS,
      name: 'Key end-markets',
      minWidth: '100px',
      fragment: '1fr',
      selected: false
    },
    {
      id: PROFILER_SEARCH_RESULTS_COLUMNS.INDUSTRY,
      name: 'Industry',
      minWidth: '60px',
      fragment: '1.5fr',
      selected: true
    },
    {
      id: PROFILER_SEARCH_RESULTS_COLUMNS.VALUE_CHAIN,
      name: 'Value chain',
      minWidth: '80px',
      fragment: '1.5fr',
      selected: true
    },
    {
      id: PROFILER_SEARCH_RESULTS_COLUMNS.PRODUCTS,
      name: 'Products',
      minWidth: '60px',
      fragment: '1.5fr',
      selected: true
    },
    {
      id: PROFILER_SEARCH_RESULTS_COLUMNS.SALES,
      name: 'Sales (€M)',
      minWidth: '90px',
      fragment: '0.5fr',
      selected: true
    },
    {
      id: PROFILER_SEARCH_RESULTS_COLUMNS.EMPLOYEES,
      name: 'Employees',
      minWidth: '65px',
      fragment: '0.5fr',
      selected: true
    },
    {
      id: PROFILER_SEARCH_RESULTS_COLUMNS.CAGR3Y,
      name: 'CAGR 3Y (%)',
      minWidth: '75px',
      fragment: '0.5fr',
      selected: isCagr3YearsPeriodSelected
    },
    {
      id: PROFILER_SEARCH_RESULTS_COLUMNS.CAGR4Y,
      name: 'CAGR 4Y (%)',
      minWidth: '75px',
      fragment: '0.5fr',
      selected: isCagr4YearsPeriodSelected
    },
    {
      id: PROFILER_SEARCH_RESULTS_COLUMNS.EBIT_MARGIN,
      name: 'EBIT Margin (%)',
      minWidth: '90px',
      fragment: '0.5fr',
      selected: isEbitMarginFilled
    },
    {
      id: PROFILER_SEARCH_RESULTS_COLUMNS.SITUATION,
      name: 'Situation',
      minWidth: '100px',
      fragment: '0.5fr',
      selected: isSituationSelected
    },
    {
      // On by default only when the search used an ownership criterion.
      id: PROFILER_SEARCH_RESULTS_COLUMNS.SHAREHOLDERS,
      name: 'Shareholders',
      minWidth: '200px',
      fragment: '1fr',
      selected: isOwnershipSelected
    },
    // NOT YET IMPLEMENTED ON BACKEND, UNCOMMENT ONCE IMPLEMENTED THERE
    // {
    //   id: PROFILER_SEARCH_RESULTS_COLUMNS.TOP_MANAGEMENT,
    //   name: 'Top management',
    //   minWidth: '120px',
    //   fragment: '1.5fr',
    //   selected: windowWidth >= 1366 ? true : false
    // },
    {
      id: PROFILER_SEARCH_RESULTS_COLUMNS.ACTIONS,
      name: 'Actions',
      minWidth: '136px',
      fragment: '136px',
      minWidthResult: '120px',
      fragmentResult: '120px',
      selected: true,
      hidden: true
    }
  ];

  // TD-2499: Lite must not see the EBIT-margin column (list, picker, or "Financials only").
  return coerceProfilerColumnsForLite(columns, hideEbitMargin);
};

/**
 * The Profiler result views live in `src/utils/constants/profilerViews.js` as of
 * TD-2733, so the saved-project and team-feedback surfaces can share one definition
 * instead of carrying a duplicate list. Re-exported here because this module is the
 * established import site for the search page and for
 * `ProfilerProjects/profilerProjectViewConstants.js`.
 */
export { DEFAULT_PROFILER_VIEW };
export {
  PROFILER_VIEWS,
  PROFILER_VIEWS_LIST,
  isProfilerListVisible
} from '../../../../utils/constants/profilerViews';
